import React, { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Printer, QrCode, ExternalLink } from "lucide-react";
import type { Vehicle } from "../../lib/types";
import { Card } from "../core/Layout";
import { Button } from "../core/Button";

export function vehicleCardUrl(id: string): string {
  return `${window.location.origin}/v/${id}`;
}

export function VehicleQrCard({ v }: { v: Vehicle }) {
  const wrap = useRef<HTMLDivElement>(null);
  const url = vehicleCardUrl(v.id);
  const canvas = () => wrap.current?.querySelector("canvas") as HTMLCanvasElement | null;
  const download = () => {
    const c = canvas();
    if (!c) return;
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `ENFLEET-QR-${v.plate_number}.png`;
    a.click();
  };
  const print = () => {
    const c = canvas();
    if (!c) return;
    const w = window.open("", "_blank", "width=420,height=560");
    if (!w) return;
    w.document.write(`<html><head><title>${v.plate_number} · ENFLEET</title><style>body{font-family:Arial,sans-serif;text-align:center;padding:24px;color:#0f172a}.plate{display:inline-block;background:#0f172a;color:#fff;font-family:monospace;font-weight:700;font-size:28px;padding:6px 18px;border-radius:6px;letter-spacing:2px}.sub{color:#475569;font-size:14px;margin-top:6px}.foot{margin-top:14px;font-size:11px;color:#64748b}</style></head><body><img src="${c.toDataURL("image/png")}" width="260" height="260"/><div><span class="plate">${v.plate_number}</span></div><div class="sub">${`${v.make || ""} ${v.model || ""}`.trim()}</div><div class="foot">Scan for vehicle card · ENFLEET by POMEN</div><script>window.onload=()=>{window.print();}</script></body></html>`);
    w.document.close();
  };
  return (
    <Card title="Vehicle QR" icon={QrCode} testId="vehicle-qr-card">
      <div className="flex flex-col items-center">
        <div ref={wrap} className="rounded-xl border border-slate-200 bg-white p-3" data-testid="vehicle-qr-canvas">
          <QRCodeCanvas value={url} size={168} level="M" marginSize={1} fgColor="#10046E" imageSettings={{ src: "/enfleet-mark.svg", height: 30, width: 30, excavate: true }} />
        </div>
        <p className="mt-3 font-mono text-sm font-bold tracking-wider text-slate-900">{v.plate_number}</p>
        <a href={url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 break-all text-center text-[11px] text-[color:var(--brand)] hover:underline" data-testid="vehicle-card-link">{url.replace(/^https?:\/\//, "")}<ExternalLink className="h-3 w-3" /></a>
        <p className="mt-2 text-center text-[11px] text-slate-500">Scanning opens a public vehicle card with plate, model and live compliance status. Stick it on the windscreen or key tag.</p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="secondary" onClick={download} data-testid="download-qr-button"><Download className="h-3.5 w-3.5" />PNG</Button>
          <Button size="sm" variant="secondary" onClick={print} data-testid="print-qr-button"><Printer className="h-3.5 w-3.5" />Print label</Button>
        </div>
      </div>
    </Card>
  );
}
